import BigNumber from 'bignumber.js';

import { ZERO } from 'modules/common';
import { ContractsManager, convertFromWei, convertToHex } from 'modules/core';
import { api, cacheTags } from 'modules/store';

export interface GetAccountMaxValuesParams {
  currentAccount: string;
  depositBalance: BigNumber;
  withdrawBalance: BigNumber;
}

interface GetAccountMaxValuesResult {
  depositMaxValue: BigNumber;
  withdrawMaxValue: BigNumber;
}

export const {
  useGetAccountMaxValuesQuery,
  endpoints: { getAccountMaxValues },
} = api.injectEndpoints({
  endpoints: build => ({
    getAccountMaxValues: build.query<
      GetAccountMaxValuesResult | null,
      GetAccountMaxValuesParams
    >({
      queryFn: async ({ currentAccount, depositBalance, withdrawBalance }) => {
        const instance = await ContractsManager.getInstance();
        const contract = await instance.getRegistryContract();
        const provider = await instance.getProvider();
        const web3 = provider.getWeb3();

        const [
          { gasLimit: depositGasLimit, gasPrice: depositWeiGasPrice },
          { gasLimit: withdrawGasLimit, gasPrice: withdrawWeiGasPrice },
        ] = await Promise.all([
          instance.getGasInfo({
            currentAccount,
            contract,
            method: 'addUserFunds',
            args: [currentAccount],
            value: depositBalance,
          }),
          instance.getGasInfo({
            currentAccount,
            contract,
            method: 'withdrawUserFunds',
            args: [convertToHex(withdrawBalance), currentAccount],
          }),
          web3.eth.getGasPrice(),
        ]);

        const depositGasPrice = convertFromWei(depositWeiGasPrice.toString());
        const withdrawGasPrice = convertFromWei(withdrawWeiGasPrice.toString());
        const depositMaxValue = depositBalance.minus(
          depositGasLimit.multipliedBy(depositGasPrice),
        );
        const withdrawMaxValue = withdrawBalance.minus(
          withdrawGasLimit.multipliedBy(withdrawGasPrice),
        );

        return {
          data: {
            depositMaxValue: depositMaxValue.isLessThan(ZERO)
              ? ZERO
              : depositMaxValue,
            withdrawMaxValue: withdrawMaxValue.isLessThan(ZERO)
              ? ZERO
              : withdrawMaxValue,
          },
        };
      },
      providesTags: [cacheTags.account],
    }),
  }),
});
